import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import QuoteProposalInfoDelegate from '../quoteProposalInfoDelegate';
import { SunLifeQuoteProposalInfoFormDelegate } from './proposalFormDelegate';

export default class SunLifeQuoteProposalInfoPageStore {
  public readonly proposalInfoDelegate: QuoteProposalInfoDelegate;
  public proposalFormDelegate: SunLifeQuoteProposalInfoFormDelegate;

  public get flowDelegate(): QuoteFlowDelegate {
    return this.proposalInfoDelegate.flowDelegate;
  }

  public get proposalForm(): SunLifeQuoteProposalInfoFormDelegate['proposalForm'] {
    return this.proposalFormDelegate.proposalForm;
  }

  public get isActionLoading(): boolean {
    return this.proposalInfoDelegate.isActionLoading;
  }

  public get isStepCompleted(): boolean {
    return this.proposalInfoDelegate.isStepCompleted;
  }

  public get canCompleteStep(): boolean {
    return this.proposalFormDelegate.isValid && this.proposalInfoDelegate.isFlowReadyToComplete;
  }

  public async getInitialData(): Promise<void> {
    const cartModule = this.flowDelegate.cartModule;

    this.flowDelegate.setIsInitialDataLoading(true);
    await this.proposalInfoDelegate.getInitialData();

    runInAction(() => {
      const cartName = cartModule.cart.cartName;
      const { effectiveDate, sic4, groupSize, incorporationDate } = cartModule.lastUpdateBrowsingCriteria;
      const { zip, state, county } = this.proposalInfoDelegate.getLocationFromCartOrGroup();
      this.proposalForm.populateFields({
        proposalName: cartName,
        effectiveDate,
        incorporationDate: incorporationDate ?? '',
        groupSize,
        zip,
        state,
        county,
        sic4,
      });
      this.flowDelegate.setIsInitialDataLoading(false);
    });
  }

  public async onComplete(): Promise<void> {
    const { proposalName, effectiveDate, zip, state, county, sic4, groupSize, incorporationDate } =
      this.proposalForm.getFormDataObject();

    await this.proposalInfoDelegate.saveProposalInfo(proposalName, {
      ...this.flowDelegate.cartModule.lastUpdateBrowsingCriteria,
      effectiveDate,
      zipCode: zip,
      state,
      county,
      sic4,
      groupSize,
      incorporationDate,
    });
  }

  constructor(params: QuoteFlowDomainParams) {
    this.proposalInfoDelegate = new QuoteProposalInfoDelegate(params);
    this.proposalFormDelegate = new SunLifeQuoteProposalInfoFormDelegate(
      this.proposalInfoDelegate.domainProductLinesDelegate,
    );

    makeAutoObservable(this, { proposalInfoDelegate: false, proposalFormDelegate: false }, { autoBind: true });
  }
}

const SunLifeQuoteProposalInfoPageStoreContext = createContext(
  new SunLifeQuoteProposalInfoPageStore({} as QuoteFlowDomainParams),
);

export const useSunLifeQuoteProposalInfoPageStore = (): SunLifeQuoteProposalInfoPageStore => {
  return useContext(SunLifeQuoteProposalInfoPageStoreContext);
};

export { SunLifeQuoteProposalInfoPageStore, SunLifeQuoteProposalInfoPageStoreContext };
